import fitCurve from 'fit-curve'

import { LegacyDatapoint, Datapoint } from '../types/datapoint'
import { DataType } from '../types/enums/eventDataType'
import { EventFlag, EventType } from '../types/enums/eventType'
import { MqttEvent } from '../types/enums/mqttEvent'
import { Log } from '../types/log'
import { LogEvent } from '../types/logEvent'
import { MqttMessage, MqttMessageV2Data } from '../types/mqttMessage'
import { Profile } from '../types/profile'
import { get } from './service'

export const mqttMessageToDatapoint = (message: MqttMessage): Datapoint => {
  return {
    msec: message.msec,
    fan: message.data.fan,
    heat: message.data.heat,
    bt: message.data.bt,
    et: message.data.et,
    target: message.data.target,
    rpm: message.data.rpm,
    crack: message.data.crack || null,
    drum_temp: message.data.rawTemp?.tc0, // TODO, this is not correct for P2000
    inlet_temp: message.data.rawTemp?.tc1, // TODO
    rtd0: message.data.rawTemp?.rtd0 || null,
    rtd1: message.data.rawTemp?.rtd1 || null,
    rtd2: message.data.rawTemp?.rtd2 || null,
    rtd3: message.data.rawTemp?.rtd3 || null,
    rtd4: message.data.rawTemp?.rtd4 || null,
    rtd5: message.data.rawTemp?.rtd5 || null,
    rtd6: message.data.rawTemp?.rtd6 || null,
    rtd7: message.data.rawTemp?.rtd7 || null,
    rtd8: message.data.rawTemp?.rtd8 || null,
    rtd9: message.data.rawTemp?.rtd9 || null,
    rtd10: message.data.rawTemp?.rtd10 || null,
    rtd11: message.data.rawTemp?.rtd11 || null,
    tc0: message.data.rawTemp?.tc0 || null,
    tc1: message.data.rawTemp?.tc1 || null,
    tc2: message.data.rawTemp?.tc2 || null,
    tc3: message.data.rawTemp?.tc3 || null,
    tc4: message.data.rawTemp?.tc4 || null,
    tc5: message.data.rawTemp?.tc5 || null,
    tc6: message.data.rawTemp?.tc6 || null,
    tc7: message.data.rawTemp?.tc7 || null,
    tc8: message.data.rawTemp?.tc8 || null,
    tc9: message.data.rawTemp?.tc9 || null,
    tc10: message.data.rawTemp?.tc10 || null,
    tc11: message.data.rawTemp?.tc11 || null,
    tc12: message.data.rawTemp?.tc12 || null,
  } as Datapoint
}

export const mqttMessageV2DataToDatapoint = (data: MqttMessageV2Data): Datapoint => {
  return {
    ...(data as Datapoint),
    ...data.temperature_sensors?.reduce((aggr, current) => {
      // Spread the array of extra sensors out in the Datapoint object
      return {
        ...aggr,
        [current.name]: current.value,
      }
    }, {}),
  }
}

export const legacyMqttMessageToEvent = (message: MqttMessage): LogEvent => {
  const MqttEventToLog = {
    [MqttEvent.Charge]: EventType.Charge,
    [MqttEvent.Drop]: EventType.Drop,
    [MqttEvent.Dryend]: EventType.Dryend,
    [MqttEvent.Manual]: EventType.Manual,
    [MqttEvent.Firstcrack]: EventType.Firstcrack,
  }

  return {
    type: message.data.event ? MqttEventToLog[message.data.event] : EventType.UNUSED,
    msec: message.msec,
    bt: null,
  }
}

const legacyDatapointToDatapoint = (datapoint: LegacyDatapoint): Datapoint => {
  return {
    ...datapoint,
    drum_temp: datapoint.drum_temp || datapoint.tc0,
    inlet_temp: datapoint.inlet_temp || datapoint.tc1,
    bt: !datapoint.precise_data && datapoint.bt ? datapoint.bt / 100 : datapoint.bt,
    et: !datapoint.precise_data && datapoint.et ? datapoint.et / 100 : datapoint.et,
    target: !datapoint.precise_data && datapoint.target ? datapoint.target / 100 : datapoint.target,
    msec: datapoint.msec || datapoint.sec * 1000,
  }
}

export const legacyDatapointsToDatapoints = (datapoints: Array<LegacyDatapoint>): Array<Datapoint> => {
  return datapoints
    .filter((x) => x.data_type === DataType.Data)
    .sort((x, y) => x.msec - y.msec)
    .map(legacyDatapointToDatapoint)
}

const legacyDatapointToEvent = (datapoint: LegacyDatapoint): LogEvent => {
  return {
    type: datapoint.event_type === undefined || datapoint.event_type === null ? EventType.UNUSED : datapoint.event_type,
    msec: datapoint.msec || datapoint.sec * 1000,
    bt: null,
  }
}

const legacyDatapointsToEvents = (datapoints: Array<LegacyDatapoint>): Array<LogEvent> => {
  return datapoints
    .filter((x) => x.data_type === DataType.Event)
    .sort((x, y) => x.msec - y.msec)
    .map(legacyDatapointToEvent)
}

export const getEventsFromLogOrDatapoints = (log: Log | null, datapoints: Array<LegacyDatapoint>): Array<LogEvent> => {
  return log && log.events.length > 0 ? log.events : legacyDatapointsToEvents(datapoints)
}

export const getLogsUrl = (machineId?: number, searchString?: string): string => {
  const searchFragment = searchString ? `&search=${searchString}` : ''
  const completedLogFlag = EventFlag.EVENT_FLAG_DRYEND | EventFlag.EVENT_FLAG_DROP
  const machineFragment = machineId ? `&machine=${machineId}` : ''
  return `logs/?event_flags=${completedLogFlag}${machineFragment}${searchFragment}`
}

export const getLogName = (logData: Log | null, machineName: string | undefined, batchString: string): string => {
  const machineNameFragment = machineName ? `${machineName} - ` : ''
  return logData?.name || `${machineNameFragment}${batchString} #${logData?.batch_no}`
}

export const loadLogProfile = (
  profileName: string,
  customerId: number,
  successCallback: (profile: Profile | null) => void,
  errorCallback: () => void
) => {
  get<Array<Profile>>(`profiles/?search=${profileName}&page_size=all&customer=${customerId}`)
    .then((result) => {
      const profile = result.data.find((x) => (x.name || '').toLowerCase() === (profileName || '').toLowerCase())
      successCallback(profile || null)
    })
    .catch(() => errorCallback())
}

export const loadLogProfileById = (
  profileId: number,
  successCallback: (profile: Profile) => void,
  errorCallback: () => void
) => {
  get<Profile>(`profiles/${profileId}/`)
    .then((result) => successCallback(result.data))
    .catch(() => errorCallback())
}

export const calculateProfilePoints = (profilePoints: Array<Array<number | null>>): number[][][] => {
  let len = 200
  let max_err = 0.5
  let rc: Array<Array<Array<number>>> = []
  while (len > 15) {
    rc = fitCurve(profilePoints as number[][], max_err)
    max_err *= 1.2
    len = rc.length
  }
  return rc
}
